import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Button, 
  ButtonGroup, 
  Tooltip, 
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Description as DocxIcon,
} from '@mui/icons-material';
import { downloadPDF, downloadDOCX } from '../../redux/slices/documentSlice';

/**
 * DocumentDownloadButtons Component
 * 
 * Provides PDF and DOCX download buttons for completed documents
 * 
 * Props:
 * - documentId: string (required) - Document ID
 * - documentTitle: string (optional) - Document title for filename
 * - status: string (required) - Document status ('completed' to show buttons)
 */
const DocumentDownloadButtons = ({ documentId, documentTitle, status }) => {
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const { downloadingPDF, downloadingDOCX } = useSelector(
    (state) => state.documents
  );

  // Only show buttons for completed documents
  if (status !== 'completed') {
    return null;
  }

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    try {
      await dispatch(
        downloadPDF({ 
          documentId, 
          documentTitle: documentTitle || 'document' 
        })
      ).unwrap();
      setSnackbar({ open: true, message: 'PDF başarıyla indirildi', severity: 'success' });
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err?.message || 'PDF indirilirken bir hata oluştu', 
        severity: 'error' 
      });
    }
  };

  // Handle DOCX download
  const handleDownloadDOCX = async () => {
    try {
      await dispatch(
        downloadDOCX({ 
          documentId, 
          documentTitle: documentTitle || 'document' 
        })
      ).unwrap();
      setSnackbar({ open: true, message: 'DOCX başarıyla indirildi', severity: 'success' });
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err?.message || 'DOCX indirilirken bir hata oluştu', 
        severity: 'error' 
      });
    }
  };

  return (
    <>
      <ButtonGroup variant="outlined" size="small">
        <Tooltip title="PDF İndir">
          <Button
            startIcon={
              downloadingPDF ? (
                <CircularProgress size={16} />
              ) : (
                <PdfIcon />
              )
            }
            onClick={handleDownloadPDF}
            disabled={downloadingPDF || downloadingDOCX}
          >
            PDF
          </Button>
        </Tooltip>
        <Tooltip title="DOCX İndir">
          <Button
            startIcon={
              downloadingDOCX ? (
                <CircularProgress size={16} />
              ) : (
                <DocxIcon />
              )
            }
            onClick={handleDownloadDOCX}
            disabled={downloadingPDF || downloadingDOCX}
          >
            DOCX
          </Button>
        </Tooltip>
      </ButtonGroup>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DocumentDownloadButtons;

